import pages from './pages.json';
import { DEFAULT_LOCALE, createLanguageRegistry, LANGUAGE_DEFINITIONS } from './registry';

export const LOCALIZED_PAGES = pages;
export const LOCALIZED_PATHS = pages.filter(page => page.priority).map(page => page.path);

// Routes use canonical registry codes, including regional and script subtags.
// Unknown prefixes and untranslated content never become translated URLs.
export function createLocaleRoutes(registry = createLanguageRegistry(LANGUAGE_DEFINITIONS)) {
  const languages = registry.list('marketing');
  const supported = languages.map(language => language.code);
  const normalizeLocale = value => registry.resolve(value, 'marketing', DEFAULT_LOCALE);
  const pathLocale = pathname => {
    const segment = String(pathname).split('/')[1];
    return supported.find(code => code.toLowerCase() === segment?.toLowerCase()) || null;
  };
  const stripLocale = pathname => {
    const normalized = String(pathname).replace(/\/+$/, '') || '/';
    if (!pathLocale(normalized)) return normalized;
    const remaining = normalized.split('/').slice(2).join('/');
    return remaining ? `/${remaining}` : '/';
  };
  const localizedPath = (pathname, locale) => {
    const path = stripLocale(pathname);
    if (!pages.some(page => page.path === path)) return path;
    const language = normalizeLocale(locale);
    return language === DEFAULT_LOCALE ? path : `/${language}${path === '/' ? '' : path}`;
  };
  return {
    supported,
    normalizeLocale,
    localeFromPath: pathname => pathLocale(pathname) || DEFAULT_LOCALE,
    unlocalizedPath: stripLocale,
    localizedPath,
    routes: () => languages.flatMap(language => pages.map(page => ({ ...page, locale: language.code, path: localizedPath(page.path, language.code) }))),
  };
}

const routes = createLocaleRoutes();
export const SUPPORTED_LOCALES = routes.supported;
export const normalizeLocale = routes.normalizeLocale;
export const localeFromPath = routes.localeFromPath;
export const unlocalizedPath = routes.unlocalizedPath;
export const localizedPath = routes.localizedPath;
export const localizedRoutes = routes.routes;

export function appUrl({ locale = DEFAULT_LOCALE, currency = 'gbp', plan, interval = 'monthly' } = {}) {
  const url = new URL(process.env.REACT_APP_APP_URL || 'https://app.noteician.com');
  url.searchParams.set('lang', normalizeLocale(locale));
  if (['individual', 'practice'].includes(plan)) {
    url.searchParams.set('plan', plan);
    url.searchParams.set('interval', interval === 'yearly' ? 'yearly' : 'monthly');
  } else {
    url.searchParams.delete('plan');
    url.searchParams.delete('interval');
  }
  url.searchParams.set('currency', currency === 'eur' ? 'eur' : 'gbp');
  return url.toString();
}
